import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import ChevronIcon from '../../../atoms/icons/ChevronIcon';
import ServiceHealthCard, { serviceHealthPropType } from './ServiceHealthCard';
import { formatStatusLabel, getStatusModifier } from '../utils/status';
import { useServiceMetricSummaries } from '../hooks/useServiceMetricSummaries';

const ServiceHealthPreview = ({ service, isExpanded, onSelect }) => {
    const { id, name, status, metrics } = service;
    const {
        uptime,
        cpuUsage,
        load,
    } = useServiceMetricSummaries(metrics);

    const statusModifier = getStatusModifier(status);
    const statusLabel = formatStatusLabel(status);
    const label = name || id;
    const panelId = `service-health-card-${id}`;

    const handleSelect = useCallback(() => {
        if (onSelect) {
            onSelect(id);
        }
    }, [id, onSelect]);

    return (
        <div className={`service-health-preview${isExpanded ? ' service-health-preview--expanded' : ''}`}>
            <button
                type="button"
                className="service-health-preview__summary"
                onClick={handleSelect}
                aria-expanded={isExpanded}
                aria-controls={panelId}
            >
                <div className="service-health-preview__heading">
                    <div className="service-health-preview__title-group">
                        <span className={`service-health-preview__status-dot service-health-preview__status-dot--${statusModifier}`} aria-hidden="true" />
                        <span className="service-health-preview__title">{label}</span>
                    </div>
                    <span className="service-health-preview__status">{statusLabel}</span>
                </div>
                <dl className="service-health-preview__metrics">
                    <div className="service-health-preview__metric">
                        <dt>Uptime</dt>
                        <dd>{uptime || 'N/A'}</dd>
                    </div>
                    <div className="service-health-preview__metric">
                        <dt>CPU</dt>
                        <dd>{cpuUsage || 'N/A'}</dd>
                    </div>
                    <div className="service-health-preview__metric">
                        <dt>Load (1m)</dt>
                        <dd>{load || 'N/A'}</dd>
                    </div>
                </dl>
                <ChevronIcon
                    size={16}
                    direction={isExpanded ? 'up' : 'down'}
                    className="service-health-preview__chevron"
                />
            </button>

            {isExpanded && (
                <div id={panelId} className="service-health-preview__details">
                    <ServiceHealthCard service={service} isSelected />
                </div>
            )}
        </div>
    );
};

ServiceHealthPreview.propTypes = {
    service: serviceHealthPropType.isRequired,
    isExpanded: PropTypes.bool,
    onSelect: PropTypes.func,
};

ServiceHealthPreview.defaultProps = {
    isExpanded: false,
    onSelect: undefined,
};

export default ServiceHealthPreview;
